/**
 * The exposure each render of a scene draws at, through one path. A scene holds its world's factor,
 * and `drawAtExposure` draws it at another, as Godot captures a reflection probe at the exposure of
 * the world and not of the camera. Each render scales every light's energy and the emission by the
 * factor it draws at, and runs each follower with it, as Godot fills its buffers for each render
 * (`light_storage.cpp:656-658`, `:884-886`, `scene_forward_clustered.glsl:1679-1683`).
 */
import { useLayoutEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { isAmbientLight } from '../ambientLight.js';
import { isLight } from '../isLight.js';
import { observeSceneExposure, observeSceneRenderEnd } from '../sceneRenderCamera.js';
import { TrackedObjects } from '../trackedObjects.js';
import { EMISSION_EXPOSURE } from './emissionExposure.js';
import { ScaledLights } from './scaledLights.js';
/**
 * Each scene's exposure, made by the first held exposure, follower or draw, and removed with the
 * last. Written only by `useSceneExposure`, `followSceneExposure` and `drawAtExposure`.
 */
// eslint-disable-next-line no-restricted-syntax -- the last holder's release deletes the scene's entry
const exposures = new WeakMap();
/** The exposure `drawAtExposure` draws a scene at, written only by it and cleared when the draw ends. */
// eslint-disable-next-line no-restricted-syntax -- drawAtExposure writes an entry for one draw and deletes it after the draw
const drawnExposures = new WeakMap();
function isExposedLight(object) {
    return isLight(object) && !isAmbientLight(object);
}
/** The exposure of `scene`, made on the first ask. The caller settles it with `settleExposure`. */
function exposureOf(scene) {
    const existing = exposures.get(scene);
    if (existing)
        return existing;
    const exposure = {
        held: [],
        followers: new Set(),
        unhookRenders: null,
        lights: null,
        scaled: new ScaledLights(),
        isRendering: false,
    };
    exposures.set(scene, exposure);
    return exposure;
}
/** Whether a render of `scene` draws at an exposure, or runs a follower. */
function needsRenderHooks(scene, exposure) {
    return (exposure.followers.size > 0 ||
        drawnExposures.has(scene) ||
        exposure.held.some(({ view }) => view !== null));
}
/** Applies the exposure to each render of `scene`, until the returned function runs. */
function hookRenders(scene, exposure) {
    const stopApplying = observeSceneExposure(scene, (camera) => applyRenderExposure(scene, exposure, camera));
    const stopRestoring = observeSceneRenderEnd(scene, () => endRender(exposure));
    return () => {
        stopApplying();
        stopRestoring();
    };
}
/**
 * Hooks the renders of `scene` while one needs the exposure, unhooks them after, and removes the
 * exposure once nothing holds it.
 */
function settleExposure(scene, exposure) {
    if (needsRenderHooks(scene, exposure)) {
        exposure.unhookRenders ??= hookRenders(scene, exposure);
        return;
    }
    endRender(exposure);
    exposure.unhookRenders?.();
    exposure.unhookRenders = null;
    if (exposure.held.length > 0)
        return;
    exposure.lights?.dispose();
    exposures.delete(scene);
}
function latestHeld(scene) {
    const held = exposures.get(scene)?.held;
    return held?.[held.length - 1];
}
/**
 * The factor of the attributes of the world of `scene` itself, or null without them. A camera of
 * the world may render it with others.
 *
 * @canonical The exposure of the attributes of a scene's own world
 */
export function worldExposureOfScene(scene) {
    return latestHeld(scene)?.world ?? null;
}
/** The exposure a render of `scene` draws at: the one `drawAtExposure` draws at, else the held one. */
function drawnExposureOf(scene) {
    const drawn = drawnExposures.get(scene);
    return drawn ? drawn.exposure : (latestHeld(scene)?.view ?? null);
}
function exposedLightsOf(scene, exposure) {
    exposure.lights ??= new TrackedObjects(scene, isExposedLight);
    return exposure.lights.current();
}
/** Puts back what the render under way changed. The emission reads 1 again, as outside any render. */
function endRender(exposure) {
    if (!exposure.isRendering)
        return;
    exposure.isRendering = false;
    exposure.scaled.restore();
    EMISSION_EXPOSURE[0] = 1;
}
/**
 * Scales each exposed light and the emission of a render of `scene` by the factor it draws at, 1
 * with no exposure, and runs each follower. A render that never ended is put back first, so a light's
 * energy is scaled once. The emission buffer reads 1 again after the render, as outside any.
 */
function applyRenderExposure(scene, exposure, camera) {
    endRender(exposure);
    exposure.isRendering = true;
    const drawn = drawnExposureOf(scene);
    const factor = drawn ?? 1;
    EMISSION_EXPOSURE[0] = factor;
    // A factor of 1 leaves every light as it holds, so most renders scale none.
    if (factor !== 1)
        exposure.scaled.scale(exposedLightsOf(scene, exposure), factor);
    for (const follower of exposure.followers)
        follower(drawn, camera);
}
/**
 * Runs `follower` in each render of `scene` with the exposure the render draws at, until the
 * returned function runs. A consumer whose state follows the exposure follows it here, so a draw
 * at another exposure shows it too.
 *
 * @canonical The state a scene draws for the exposure of a render
 */
export function followSceneExposure(scene, follower) {
    const exposure = exposureOf(scene);
    exposure.followers.add(follower);
    settleExposure(scene, exposure);
    return () => {
        exposure.followers.delete(follower);
        settleExposure(scene, exposure);
    };
}
/**
 * Runs `draw` with each render of `scene` at `exposure`, null for none. Godot gives each render the
 * exposure of its own camera attributes, so a held exposure of 0 leaves a draw at another lit.
 *
 * @canonical The state a scene draws for the exposure of a render
 */
export function drawAtExposure(scene, exposure, draw) {
    const outer = drawnExposures.get(scene);
    drawnExposures.set(scene, { exposure });
    settleExposure(scene, exposureOf(scene));
    try {
        return draw();
    }
    finally {
        if (outer)
            drawnExposures.set(scene, outer);
        else
            drawnExposures.delete(scene);
        settleExposure(scene, exposureOf(scene));
    }
}
/**
 * Renders the scene of the enclosing world with `factor` until the world unmounts, and answers
 * `worldFactor` as the factor of its own attributes. A null factor draws at 1.
 *
 * @canonical The exposure a scene renders with
 */
export function useSceneExposure(factor, worldFactor) {
    const scene = useThree((state) => state.scene);
    // A layout effect, so a render after the commit draws at the new factor.
    useLayoutEffect(() => {
        const entry = { view: factor, world: worldFactor };
        const exposure = exposureOf(scene);
        exposure.held.push(entry);
        settleExposure(scene, exposure);
        return () => {
            exposure.held.splice(exposure.held.indexOf(entry), 1);
            settleExposure(scene, exposure);
        };
    }, [scene, factor, worldFactor]);
}
//# sourceMappingURL=sceneExposure.js.map